import { PROCEDURE_TYPES, type ProcedureType } from '@/types/cgt'

/** The route of the Overall Status board. */
export const BOARD_PATH = '/board'

/**
 * Board status filter values. `open` is the default: not complete and not
 * cancelled. Complete is DERIVED from steps, because `Procedure.status` is
 * never written `completed`, so this filter runs in the browser.
 */
export type BoardStatusFilter =
  | 'open'
  | 'not_started'
  | 'in_progress'
  | 'pending_review'
  | 'expired'
  | 'complete'
  | 'cancelled'
  | 'all'

export const BOARD_STATUS_OPTIONS: { value: BoardStatusFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'pending_review', label: 'Pending review' },
  // Cleaning only (z8nrz7f9u5): a clean not done on its day, from the next day.
  { value: 'expired', label: 'Expired' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'Any status' },
]

export interface BoardFilters {
  q: string
  /** '' = every patient. */
  patientMrn: string
  status: BoardStatusFilter
  /**
   * '' = anyone; a user id; or `ASSIGNED` / `NOT_ASSIGNED` — the supervisor's
   * "who has nothing yet" list (z8nrz7f9u5).
   */
  assigneeId: string
  /** '' = every customer. Only Kit and MES runs carry one (z8nrz7e71k). */
  customerId: string
  /** `YYYY-MM-DD`, '' = unbounded. */
  createdFrom: string
  createdTo: string
  /** Empty = every type. */
  types: ProcedureType[]
}

export const DEFAULT_BOARD_FILTERS: BoardFilters = {
  q: '',
  patientMrn: '',
  status: 'open',
  assigneeId: '',
  customerId: '',
  createdFrom: '',
  createdTo: '',
  types: [],
}

/** The Assignee filter's two non-user values (no user id has this shape). */
export const ASSIGNED = 'assigned'
export const NOT_ASSIGNED = 'unassigned'

const STATUS_VALUES = new Set<string>(BOARD_STATUS_OPTIONS.map(o => o.value))
const TYPE_VALUES = new Set<string>(PROCEDURE_TYPES)
const DAY = /^\d{4}-\d{2}-\d{2}$/

/** Board url for all types, or filtered to one. */
export function boardPath(type?: ProcedureType): string {
  return type ? `${BOARD_PATH}?type=${type}` : BOARD_PATH
}

/**
 * The applied filters, read from the url.
 *
 * Unknown values are dropped rather than trusted: a hand-edited or stale link
 * must land on a working board, not on an error from the server.
 */
export function parseBoardSearch(params: URLSearchParams): BoardFilters {
  const status = params.get('status') ?? ''
  const day = (key: string) => {
    const v = params.get(key) ?? ''
    return DAY.test(v) ? v : ''
  }
  const types = (params.get('type') ?? '')
    .split(',')
    .map(t => t.trim())
    .filter((t): t is ProcedureType => TYPE_VALUES.has(t))
  return {
    q: params.get('q')?.trim() ?? '',
    patientMrn: params.get('patient') ?? '',
    status: STATUS_VALUES.has(status) ? (status as BoardStatusFilter) : DEFAULT_BOARD_FILTERS.status,
    assigneeId: params.get('assignee') ?? '',
    customerId: params.get('customer') ?? '',
    createdFrom: day('from'),
    createdTo: day('to'),
    types: [...new Set(types)],
  }
}

/** The url search for these filters, omitting every default so links stay short. */
export function boardSearchFor(filters: BoardFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.q.trim()) params.set('q', filters.q.trim())
  if (filters.patientMrn) params.set('patient', filters.patientMrn)
  if (filters.status !== DEFAULT_BOARD_FILTERS.status) params.set('status', filters.status)
  if (filters.assigneeId) params.set('assignee', filters.assigneeId)
  if (filters.customerId) params.set('customer', filters.customerId)
  if (filters.createdFrom) params.set('from', filters.createdFrom)
  if (filters.createdTo) params.set('to', filters.createdTo)
  if (filters.types.length > 0) params.set('type', filters.types.join(','))
  return params
}

export function sameBoardFilters(a: BoardFilters, b: BoardFilters): boolean {
  return boardSearchFor(a).toString() === boardSearchFor(b).toString()
}

/**
 * The part of the filters the server applies. No type means every type, sent
 * explicitly so the server's default exclusions do not apply.
 *
 * Status is split. `cancelled` is a stored status, so the server filters it.
 * Every other board status is derived in the browser from runs still stored as
 * `in_progress` (nothing writes `completed`), so the server narrows as far as
 * it safely can and the browser derives the rest:
 *
 * - `pending_review` has a server filter of its own (z8nrz7dt41).
 * - The server's `in_progress` EXCLUDES runs pending review — the procedure
 *   tables' "In progress" must not list a row whose chip says "Pending
 *   review". So "open", which includes them, cannot narrow to it and asks for
 *   everything; the browser drops cancelled and complete runs itself. Asking
 *   for `in_progress` here is what emptied the board of pending-review runs.
 * - `not_started`, `in_progress` and `complete` never include a pending-review
 *   run, so `in_progress` is exactly right for them.
 */
export function boardServerQuery(filters: BoardFilters) {
  return {
    status:
      filters.status === 'all' || filters.status === 'open'
        ? undefined
        : filters.status === 'cancelled'
          ? ('cancelled' as const)
          : filters.status === 'pending_review'
            ? ('pending_review' as const)
            : ('in_progress' as const),
    q: filters.q.trim() || undefined,
    patientMrn: filters.patientMrn || undefined,
    assigneeId:
      filters.assigneeId && filters.assigneeId !== ASSIGNED && filters.assigneeId !== NOT_ASSIGNED
        ? filters.assigneeId
        : undefined,
    assigned: filters.assigneeId === ASSIGNED ? true : filters.assigneeId === NOT_ASSIGNED ? false : undefined,
    customerId: filters.customerId || undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    types: filters.types.length > 0 ? filters.types : PROCEDURE_TYPES,
  }
}
